import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  Descriptions,
  Table,
  Tag,
  Space,
  Button,
  Input,
  Alert,
  Typography,
  Divider,
  Popconfirm,
  Row,
  Col,
  Card,
  Statistic,
  Form,
  DatePicker,
  InputNumber,
} from 'antd';
import {
  CheckCircleOutlined,
  SendOutlined,
  FileDoneOutlined,
  CloseCircleOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { hasPermission } from '../../utils/permissions';
import {
  fetchReturnOrderRequest,
  approveReturnOrderRequest,
  updateReturnOrderStatusRequest,
  createCreditNoteRequest,
} from '../../redux/actions/returnOrderActions';

const { Text } = Typography;
const { TextArea } = Input;

const fmtMoney = (v) => (v != null ? `₹ ${Number(v).toFixed(2)}` : '—');
const fmtDate  = (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—');
const fmtDateTime = (v) => (v ? dayjs(v).format('DD MMM YYYY, hh:mm A') : '—');

export const getReturnStatusTag = (status) => {
  const map = {
    draft:      { color: 'default', text: 'Draft'      },
    approved:   { color: 'blue',    text: 'Approved'   },
    dispatched: { color: 'gold',    text: 'Dispatched' },
    completed:  { color: 'success', text: 'Completed'  },
    cancelled:  { color: 'error',   text: 'Cancelled'  },
  };
  const cfg = map[status] || { color: 'default', text: status || '—' };
  return <Tag color={cfg.color} style={{ fontWeight: 500 }}>{cfg.text}</Tag>;
};

/**
 * ReturnOrderDetail
 *
 * Props:
 *  open            — boolean
 *  onClose         — callback
 *  returnOrderId   — uint
 */
const ReturnOrderDetail = ({ open, onClose, returnOrderId }) => {
  const dispatch = useDispatch();
  const permissions = useSelector((s) => s.auth.permissions || []);
  const { currentReturnOrder, actionLoading, loading } = useSelector((s) => s.returns);

  const canUpdate  = hasPermission(permissions, 'delivery_challan/update');
  const canCreateCN = hasPermission(permissions, 'bill_receipt/create');

  const [cnForm] = Form.useForm();
  const [showCNForm, setShowCNForm] = useState(false);

  useEffect(() => {
    if (open && returnOrderId) {
      dispatch(fetchReturnOrderRequest(returnOrderId));
    }
  }, [open, returnOrderId, dispatch]);

  const ro = currentReturnOrder?.id === returnOrderId ? currentReturnOrder : null;

  const handleApprove = () => dispatch(approveReturnOrderRequest(returnOrderId));

  const handleStatusUpdate = (status) =>
    dispatch(updateReturnOrderStatusRequest(returnOrderId, status));

  const handleCreateCreditNote = () => {
    cnForm.validateFields().then((values) => {
      dispatch(
        createCreditNoteRequest({
          return_order_id:   returnOrderId,
          credit_note_date:  values.credit_note_date.format('YYYY-MM-DD'),
          amount:            values.amount,
          remarks:           values.remarks || '',
          attachment_url:    values.attachment_url || '',
        })
      );
      setShowCNForm(false);
      cnForm.resetFields();
    });
  };

  const itemColumns = [
    { title: '#', key: 'idx', width: 36,
      render: (_, __, i) => <Text type="secondary" style={{ fontSize: 11 }}>{i + 1}</Text> },
    { title: 'Product', dataIndex: 'product_name', key: 'product_name',
      render: (v, r) => (
        <div>
          <Text strong style={{ fontSize: 13 }}>{v}</Text>
          {r.unit && <Tag style={{ marginLeft: 4, fontSize: 10 }} color="geekblue">{r.unit}</Tag>}
        </div>
      ),
    },
    { title: 'Ret Qty', dataIndex: 'return_quantity', key: 'return_quantity',
      width: 90, align: 'right', render: (v) => Number(v).toFixed(2) },
    { title: 'Rate', dataIndex: 'rate', key: 'rate',
      width: 100, align: 'right', render: (v) => fmtMoney(v) },
    { title: 'GST %', dataIndex: 'tax_slab', key: 'tax_slab',
      width: 70, align: 'center',
      render: (v) => v ? <Tag color="orange">{v}%</Tag> : '0%' },
    { title: 'Amount', dataIndex: 'amount', key: 'amount',
      width: 110, align: 'right',
      render: (v) => <Text strong style={{ color: '#cf1322', fontSize: 13 }}>{fmtMoney(v)}</Text> },
    { title: 'Quality Remarks', dataIndex: 'quality_remarks', key: 'quality_remarks',
      render: (v) => v ? <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text> : null },
  ];

  return (
    <Modal
      title={
        <Space>
          Return Order {ro?.return_order_number || `#${returnOrderId}`}
          {ro && getReturnStatusTag(ro.status)}
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
      width={860}
      destroyOnClose
      styles={{ body: { maxHeight: '80vh', overflowY: 'auto' } }}
    >
      {loading && !ro ? (
        <div style={{ textAlign: 'center', padding: 40 }}>Loading…</div>
      ) : !ro ? null : (
        <>
          {/* ── Summary ─────────────────────────────────────────────── */}
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={6}>
              <Card size="small" style={{ background: '#fff2f0', borderColor: '#ffccc7' }}>
                <Statistic title="Total Return Amount" value={Number(ro.total_amount).toFixed(2)}
                  prefix="₹" valueStyle={{ fontSize: 15, color: '#cf1322' }} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Items</div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{ro.items?.length || 0}</div>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>PO #</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1890ff' }}>#{ro.purchase_order_id}</div>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Vendor</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{ro.vendor_name}</div>
              </Card>
            </Col>
          </Row>

          <Descriptions size="small" bordered column={{ xs: 1, sm: 2 }} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Return #"><Text strong>{ro.return_order_number}</Text></Descriptions.Item>
            <Descriptions.Item label="Return Date">{fmtDate(ro.return_date)}</Descriptions.Item>
            <Descriptions.Item label="Return Reason" span={2}>
              <Text style={{ color: '#595959' }}>{ro.return_reason}</Text>
            </Descriptions.Item>
            {ro.remarks && (
              <Descriptions.Item label="Remarks" span={2}>{ro.remarks}</Descriptions.Item>
            )}
            {ro.delivery_challan_id && (
              <Descriptions.Item label="Challan #">#{ro.delivery_challan_id}</Descriptions.Item>
            )}
            <Descriptions.Item label="Created By">{ro.created_by_name || '—'}</Descriptions.Item>
            {ro.approved_by_name && (
              <>
                <Descriptions.Item label="Approved By">{ro.approved_by_name}</Descriptions.Item>
                <Descriptions.Item label="Approved At">{fmtDateTime(ro.approved_at)}</Descriptions.Item>
              </>
            )}
            {ro.credit_note_id && (
              <Descriptions.Item label="Credit Note ID">
                <Tag color="success">CN #{ro.credit_note_id}</Tag>
              </Descriptions.Item>
            )}
          </Descriptions>

          {/* ── Items ───────────────────────────────────────────────── */}
          <Divider orientation="left" style={{ fontSize: 13 }}>
            Return Items ({ro.items?.length || 0})
          </Divider>
          <Table
            columns={itemColumns}
            dataSource={ro.items || []}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ x: 640 }}
          />

          {/* ── Action Buttons ───────────────────────────────────────── */}
          {ro.status !== 'completed' && ro.status !== 'cancelled' && (canUpdate || canCreateCN) && (
            <>
              <Divider style={{ margin: '16px 0 12px' }} />

              <Space wrap>
                {/* Approve (draft → approved) */}
                {canUpdate && ro.status === 'draft' && (
                  <Popconfirm title="Approve this return order?" onConfirm={handleApprove} okText="Yes, Approve">
                    <Button type="primary" icon={<CheckCircleOutlined />} loading={actionLoading}>
                      Approve
                    </Button>
                  </Popconfirm>
                )}

                {/* Mark Dispatched (approved → dispatched) */}
                {canUpdate && ro.status === 'approved' && (
                  <Popconfirm title="Mark as dispatched?" onConfirm={() => handleStatusUpdate('dispatched')} okText="Yes">
                    <Button icon={<SendOutlined />} loading={actionLoading}>
                      Mark Dispatched
                    </Button>
                  </Popconfirm>
                )}

                {/* Create Credit Note (approved/dispatched) */}
                {canCreateCN && ['approved', 'dispatched'].includes(ro.status) && !ro.credit_note_id && (
                  <Button
                    type="primary"
                    icon={<DollarOutlined />}
                    onClick={() => setShowCNForm(true)}
                    style={{ background: '#52c41a', borderColor: '#52c41a' }}
                  >
                    Record Credit Note
                  </Button>
                )}

                {/* Cancel */}
                {canUpdate && !['completed', 'cancelled'].includes(ro.status) && (
                  <Popconfirm
                    title="Cancel this return order?"
                    onConfirm={() => handleStatusUpdate('cancelled')}
                    okText="Yes, Cancel"
                    okType="danger"
                  >
                    <Button danger icon={<CloseCircleOutlined />} loading={actionLoading}>
                      Cancel
                    </Button>
                  </Popconfirm>
                )}
              </Space>

              {/* Credit Note Form */}
              {showCNForm && (
                <div style={{
                  marginTop: 16, padding: '16px',
                  background: '#f6ffed', borderRadius: 6,
                  border: '1px solid #b7eb8f',
                }}>
                  <Text strong style={{ fontSize: 13 }}>
                    <DollarOutlined style={{ marginRight: 6, color: '#52c41a' }} />
                    Record Credit Note from Vendor
                  </Text>
                  <Form form={cnForm} layout="vertical" style={{ marginTop: 12 }}>
                    <Row gutter={12}>
                      <Col xs={24} sm={8}>
                        <Form.Item name="credit_note_date" label="Credit Note Date"
                          rules={[{ required: true, message: 'Date required' }]}
                          initialValue={dayjs()}
                        >
                          <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item name="amount" label="Amount (₹)"
                          rules={[{ required: true, message: 'Amount required' }, { type: 'number', min: 0.01 }]}
                          initialValue={ro.total_amount}
                        >
                          <InputNumber style={{ width: '100%' }} min={0.01} precision={2} prefix="₹" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item name="attachment_url" label="Attachment URL (optional)">
                          <Input placeholder="https://…" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item name="remarks" label="Remarks">
                      <Input.TextArea rows={2} placeholder="Notes about this credit note…" />
                    </Form.Item>
                    <Space>
                      <Button type="primary" icon={<FileDoneOutlined />}
                        loading={actionLoading} onClick={handleCreateCreditNote}>
                        Save Credit Note
                      </Button>
                      <Button onClick={() => { setShowCNForm(false); cnForm.resetFields(); }}>Cancel</Button>
                    </Space>
                  </Form>
                </div>
              )}
            </>
          )}

          {ro.status === 'completed' && (
            <Alert type="success" showIcon icon={<FileDoneOutlined />}
              message={`Return process completed${ro.credit_note_id ? ` — Credit Note #${ro.credit_note_id} recorded` : ''}.`}
              style={{ marginTop: 12 }}
            />
          )}
          {ro.status === 'cancelled' && (
            <Alert type="error" showIcon icon={<CloseCircleOutlined />}
              message="This return order has been cancelled."
              style={{ marginTop: 12 }}
            />
          )}
        </>
      )}
    </Modal>
  );
};

export default ReturnOrderDetail;
